import React from 'react';
import {
	List,
	Datagrid,
	TextField,
	BooleanField,
	NumberField,
	ReferenceArrayField,
	SingleFieldList,
	ChipField,
	ReferenceArrayInput,
	AutocompleteArrayInput,
	Edit,
	Create,
	SimpleForm,
	TextInput,
	NumberInput,
	BooleanInput,
	Filter,
	DeleteWithConfirmButton,
	required,
} from 'react-admin';

// Cas d'usage partenaire — contenu de la section « Cas d'usage » de l'espace partenaire
// (ui-partners, route /cas-d-usage). Servi par api-partners.
//
// ⚠️ `published` gouverne ce que voient les partenaires : l'API ne leur sert que les
// fiches publiées. Une fiche décochée est donc invisible chez eux, pas seulement grisée.

const UseCaseFilter: React.FC = (props: any) => (
	<Filter {...props}>
		<TextInput source="q" label="Rechercher" alwaysOn />
		<ReferenceArrayInput source="themeId" reference="use-case-themes" label="Thème">
			<AutocompleteArrayInput optionText="name" />
		</ReferenceArrayInput>
	</Filter>
);

// #region UseCaseList
export const UseCaseList: React.FC = (props: any) => (
	<List {...props} filters={<UseCaseFilter />} sort={{ field: 'position', order: 'ASC' }}>
		<Datagrid rowClick="edit">
			<NumberField source="position" label="Ordre" />
			<TextField source="title" label="Titre" />
			{/* Les thèmes viennent déjà chargés dans la réponse (`themes`), mais on passe
			    par ReferenceArrayField sur `themeIds` : c'est ce que react-admin sait
			    rafraîchir après une modification de thème, sans recharger la liste. */}
			<ReferenceArrayField source="themeIds" reference="use-case-themes" label="Thèmes">
				<SingleFieldList linkType={false}>
					<ChipField source="name" size="small" />
				</SingleFieldList>
			</ReferenceArrayField>
			<TextField source="url" label="Lien" />
			<BooleanField source="published" label="Publié" />
			{/* DeleteWithConfirmButton et pas DeleteButton : le défaut react-admin est
			    « optimiste avec annulation » — la ligne disparaît tout de suite et la
			    seule issue est un bandeau « Annuler » fugace. Ici la suppression demande
			    une confirmation explicite AVANT d'écrire. Le clic ne déclenche pas
			    l'édition de la ligne (le contrôleur stoppe la propagation). */}
			<DeleteWithConfirmButton
				confirmTitle="Supprimer ce cas d'usage ?"
				confirmContent="La fiche disparaîtra de l'espace partenaire. Pour la retirer temporairement, décoche plutôt « Publié »."
			/>
		</Datagrid>
	</List>
);
// #endregion

// #region useCaseForm — champs communs création / édition
const useCaseForm = (
	<>
		<TextInput source="title" label="Titre" validate={required()} fullWidth />
		<TextInput source="summary" label="Résumé" multiline rows={3} fullWidth helperText="Une à deux phrases, affichées sous le titre côté partenaire." />
		<ReferenceArrayInput source="themeIds" reference="use-case-themes">
			<AutocompleteArrayInput label="Thèmes" optionText="name" fullWidth />
		</ReferenceArrayInput>
		<TextInput
			source="url"
			label="Lien du support"
			fullWidth
			helperText="URL de partage Google Slides en LECTURE SEULE. Laisser vide tant que le support n'est pas prêt : la fiche s'affiche alors inactive, pas en lien mort."
		/>
		<NumberInput source="position" label="Ordre d'affichage" defaultValue={0} helperText="Croissant. À égalité, tri par titre." />
		<BooleanInput
			source="published"
			label="Publié"
			defaultValue={false}
			helperText="Décoché, la fiche n'est pas servie aux partenaires."
		/>
	</>
);
// #endregion

// #region UseCaseEdit
export const UseCaseEdit: React.FC = (props: any) => (
	<Edit {...props}>
		<SimpleForm>{useCaseForm}</SimpleForm>
	</Edit>
);
// #endregion

// #region UseCaseCreate
// redirect="list" : après création, on revient à la liste et pas sur la fiche qu'on
// vient d'écrire (défaut react-admin). On crée ces fiches en série, et l'ordre
// d'affichage ne se juge que dans la liste.
export const UseCaseCreate: React.FC = (props: any) => (
	<Create {...props} redirect="list">
		<SimpleForm>{useCaseForm}</SimpleForm>
	</Create>
);
// #endregion
